import {
  ColumnMenuOption,
  DropdownButtonV2,
  useAuth,
} from '@labrav/react-components'
import { useState, useMemo } from 'react'
import { useDispatch, useSelector } from '../../../store'
import { useNavigate, useParams } from 'react-router-dom'
import { useAccountId } from '@labrav/common'
import { ChannelOfferCreationModal } from '../ChannelOfferCreationModal'
import { SubscriptionSelectionModal } from '../../Subscription/SubscriptionSelectionModal/SubscriptionSelectionModal'
import type { ModalSubscriptionsState } from '../../../modules/modalSubscription/reducer'
import { getActiveReceivedResaleAuthorizations } from '../../../modules/receivedResellerAuthorization/action'
import {
  providerToAuthCloud,
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
} from '@labrav/common'
import { Tooltip, Typography } from '@mui/material'
import type { ChannelOffersDropDownProps } from './types'

export type { ChannelOffersDropDownProps }

export const ChannelOffersDropDownWithModalTrigger: React.FC<
  ChannelOffersDropDownProps
> = ({ dropDownButtonText }) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const dispatch = useDispatch()

  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()

  const selectedActiveReceivedResellerAuthorization = useSelector<string>(
    state =>
      state?.receivedResellerAuthorization
        ?.selectedActiveReceivedResellerAuthorization
  )

  const defaultAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || defaultAccountId
  const selectedCloud = useCurrentCloudProvider()

  const { subscriptions } = useSelector<ModalSubscriptionsState>(
    state => state.modalSubscription
  )

  const loadingActiveResellerAuth = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.ACTIVE_RESELLER_AUTHORIZATION)
  )

  const handleClick = () => {
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const [selectedSubscription, setSelectedSubscription] = useState<string>('')
  const [cppoSubscription, setCppoSubscription] = useState<string>('')

  const acceptSelectedSubscription = async (subscriptionId: string) => {
    const selectedSubscriptionItem = subscriptions.find(
      subscriptionItem => subscriptionItem.subscriptionId === subscriptionId
    )

    if (selectedSubscriptionItem?.offerType === 'CPPO') {
      dispatch(
        getActiveReceivedResaleAuthorizations({
          partnerId: accountId,
          subscriptionId,
        })
      )
      await setSelectedSubscription(subscriptionId)
      await setCppoSubscription('create')
    } else {
      setIsModalOpen(false)
      setSelectedSubscription(subscriptionId)

      const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()

      const subscriptionPath =
        routeCloud && accountId
          ? `/flyout/${cloudPath}/${accountId}/subscriptions/${subscriptionId}/edit`
          : `/flyout/subscriptions/${subscriptionId}/edit`

      navigate(subscriptionPath, {
        state: {
          subscriptionId: subscriptionId,
        },
      })
    }
  }

  const handleNextChannelOffer = (subscriptionId?: string) => {
    const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()

    const channelOfferPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/subscriptions/${subscriptionId}/authorization/${selectedActiveReceivedResellerAuthorization}/edit`
        : `/flyout/subscriptions/${subscriptionId}/authorization/${selectedActiveReceivedResellerAuthorization}/edit`

    navigate(channelOfferPath)
  }

  const handleCreateListingSelection = (option: ColumnMenuOption) => {
    if (option?.key === 'create-new-channel-offer') {
      setOpen(option.key)
    } else if (option?.key === 'create-agreement-based-offer') {
      handleClick()
    } else {
      option?.route && navigate(option?.route)
    }
  }

  const handleNext = () => {
    const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()

    const channelOfferEditPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/authorization/${selectedActiveReceivedResellerAuthorization}/channel-offers/edit`
        : `/flyout/authorization/${selectedActiveReceivedResellerAuthorization}/channel-offers/edit`

    navigate(channelOfferEditPath)
  }

  const { loading: authLoading, canDo } = useAuth()
  const cloud = useCurrentCloudProvider()
  const canCreatePrivateOffer = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(cloud.toLocaleLowerCase()),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloud])

  return (
    <div data-testid="cppo-dropdown-with-modal-container">
      <Tooltip
        title={
          !canCreatePrivateOffer ? (
            <Typography>
              This action is disabled because you have read-only access
            </Typography>
          ) : (
            ''
          )
        }
      >
        <span>
          <DropdownButtonV2
            variant="contained"
            styleType="primary"
            buttonText={dropDownButtonText || 'Create'}
            data-testid={'create-cppo'}
            disabled={!canCreatePrivateOffer}
            options={[
              {
                label: 'Create new Channel offer',
                route: '',
                disable: false,
                key: 'create-new-channel-offer',
              },
              {
                label: 'Create agreement based offer',
                route: '',
                key: 'create-agreement-based-offer',
                disable: false,
                disabledTooltip: 'Coming soon',
              },
            ]}
            onOptionClick={handleCreateListingSelection}
          />
        </span>
      </Tooltip>

      <ChannelOfferCreationModal
        open={open || cppoSubscription}
        setOpen={open ? setOpen : setCppoSubscription}
        handleNext={open ? handleNext : handleNextChannelOffer}
        subscriptionId={selectedSubscription}
      />

      {isModalOpen && (
        <SubscriptionSelectionModal
          open={isModalOpen}
          handleModalClose={handleModalClose}
          acceptSelectedSubscription={acceptSelectedSubscription}
          currentSelectedSubscriptionId={selectedSubscription}
          acceptLoading={loadingActiveResellerAuth}
          type={'MODAL'}
          offerType={'CPPO'}
        />
      )}
    </div>
  )
}
